import { type ReactNode, useMemo } from 'react'
import { isNotDefined, isNotEmpty, type CompanyId, type UserProfileData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Menu } from '@mantine/core'
import { getBrowsableDashboardTeams, getDashboardManageTier, useTeams, useTranslation } from '@front/model'
import { MaterialSymbolIcon, TButton } from '@components'
import type { DashboardScopeTarget } from './types'
import { DashboardUserSearchModal } from './DashboardUserSearchModal'
import { DashboardTeamPickerModal } from './DashboardTeamPickerModal'
import { DashboardRolePickerModal } from './DashboardRolePickerModal'

type DashboardScopePickerProps = {
  companyId?: CompanyId
  companyName?: string
  selectedTarget?: DashboardScopeTarget
  user?: UserProfileData
  onSelect: (target?: DashboardScopeTarget) => void
}

export const DashboardScopePicker = ({
  companyId,
  companyName,
  selectedTarget,
  user,
  onSelect,
}: DashboardScopePickerProps): ReactNode => {
  const { t } = useTranslation()
  const tier = getDashboardManageTier(user?.permissionMap)
  const showsScopePicker = tier !== 'selfOnly'
  const canBrowseCompany = tier === 'company'
  const { data: teams = [] } = useTeams({ enabled: showsScopePicker })
  const [teamPickerOpen, teamPicker] = useDisclosure(false)
  const [rolePickerOpen, rolePicker] = useDisclosure(false)
  const [searchOpen, searchPicker] = useDisclosure(false)

  const managedTeamIds = user?.teams?.manages
  const browsableTeams = useMemo(
    () => getBrowsableDashboardTeams({ teams, managedTeamIds, tier }),
    [managedTeamIds, teams, tier]
  )
  const companyTarget = useMemo<DashboardScopeTarget | undefined>(() => {
    if (isNotDefined(companyId)) return undefined
    return { entityId: companyId, name: companyName ?? t('Company'), kind: 'company' }
  }, [companyId, companyName, t])

  if (!showsScopePicker) return

  const scopeTitle = selectedTarget?.name ?? t('Yourself')

  return (
    <>
      <Menu shadow='md' width={220} position='bottom-end'>
        <Menu.Target>
          <TButton icon='account_tree' variant='secondary'>
            {scopeTitle}
          </TButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<MaterialSymbolIcon icon='person' size='md' />} onClick={() => onSelect()}>
            {t('Yourself')}
          </Menu.Item>
          {isNotEmpty(browsableTeams) && (
            <Menu.Item leftSection={<MaterialSymbolIcon icon='groups' size='md' />} onClick={teamPicker.open}>
              {t('Teams')}
            </Menu.Item>
          )}
          {canBrowseCompany && (
            <Menu.Item leftSection={<MaterialSymbolIcon icon='badge' size='md' />} onClick={rolePicker.open}>
              {t('Roles')}
            </Menu.Item>
          )}
          {canBrowseCompany && companyTarget && (
            <Menu.Item
              leftSection={<MaterialSymbolIcon icon='domain' size='md' />}
              onClick={() => onSelect(companyTarget)}
            >
              {companyTarget.name}
            </Menu.Item>
          )}
          {canBrowseCompany && (
            <Menu.Item leftSection={<MaterialSymbolIcon icon='search' size='md' />} onClick={searchPicker.open}>
              {t('Users')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <DashboardTeamPickerModal
        opened={teamPickerOpen}
        teams={browsableTeams}
        onClose={teamPicker.close}
        onSelect={(target) => {
          teamPicker.close()
          onSelect(target)
        }}
      />
      <DashboardUserSearchModal
        opened={searchOpen}
        onClose={searchPicker.close}
        onSelect={(target) => {
          searchPicker.close()
          onSelect(target)
        }}
      />
      <DashboardRolePickerModal
        companyId={companyId}
        opened={rolePickerOpen}
        onClose={rolePicker.close}
        onSelect={(target) => {
          rolePicker.close()
          onSelect(target)
        }}
      />
    </>
  )
}
